import React, { MouseEventHandler, useContext } from "react";
import { TableRowContext } from "common/components/Table/TableRowProvider";
import { List } from "immutable";
import { ALLOWED_ACTION_BUTTONS, ActionButton } from "common/components/Table/Tr";
import { Button } from "common/components/Button";
import Can from "common/components/Can";
import { Tooltip } from "common/components/Tooltip";
import { ActionButtonView } from "common/components/Table/ActionButtonView";
import { EmptyWrapper } from "common/helpers/Components";

export interface TableRowEditorProps {
  accordionContent?: React.JSX.Element;
  actionButtons?: List<ActionButton>;
  dragHandle?: Object;
  accordionOpen?: boolean;
  persistentContent?: React.ReactNode;
  setAccordionOpen?: (accordionOpen: boolean) => void;
}

export const TableRowEditor: React.FC<TableRowEditorProps> = ({
  dragHandle,
  actionButtons = List<ActionButton>(),
  accordionContent,
  accordionOpen,
  persistentContent,
  setAccordionOpen,
}) => {
  const { isHeaderRow } = useContext(TableRowContext);

  const handleToggle: MouseEventHandler<HTMLDivElement> = (e) => {
    // Don't let the accordion toggle bubble to a clickable Tr's row onClick.
    e.stopPropagation();
    if (setAccordionOpen) {
      setAccordionOpen(!accordionOpen);
    }
  };

  return (
    <>
      {!isHeaderRow && (
        <>
          {dragHandle && (
            <div className="absolute left-0 top-0 bottom-0 flex items-center p-lg invisible group-hover/tr:visible">
              <span
                {...dragHandle}
                className="material-symbols-outlined text-secondary-400 bg-secondary-100 rounded-3xl text-lg p-xs">
                drag_indicator
              </span>
            </div>
          )}
          <div className="absolute right-0 top-0 bottom-0 flex items-center p-lg gap-sm">
            {persistentContent}
            {!actionButtons.isEmpty() && (
              <div className="flex items-center gap-sm invisible group-hover/tr:visible">
                {actionButtons
                  .filter((b) => ALLOWED_ACTION_BUTTONS.includes(b.kind))
                  .map((button, index) => {
                    const acl = button.acl || false;
                    const AclWrapper = acl
                      ? ({ children }: { children: React.ReactNode }) => (
                          <Can resource={acl[0]} permission={acl[1]}>
                            {children}
                          </Can>
                        )
                      : EmptyWrapper;

                    const ToolTipWrapper = button.tooltipTitle
                      ? ({ children }: { children: React.ReactNode }) => (
                          <Tooltip title={button.tooltipTitle} size="md" description="" variant="topArrow">
                            {children}
                          </Tooltip>
                        )
                      : EmptyWrapper;

                    return (
                      <AclWrapper key={index}>
                        <ToolTipWrapper>
                          {button.kind === "activate" ? (
                            <Button analyticsAction="activate" onClick={button.onClick} size="sm" variant="action">
                              Activate
                            </Button>
                          ) : (
                            <ActionButtonView button={button} />
                          )}
                        </ToolTipWrapper>
                      </AclWrapper>
                    );
                  })}
              </div>
            )}
            {accordionContent && (
              <span className="material-symbols-outlined text-secondary-900 cursor-pointer" onClick={handleToggle}>
                {accordionOpen ? "expand_less" : "expand_more"}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
};
